import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { useTranslation } from 'next-i18next';
import styles from './style.module.scss';
import LoadWhenVisible from 'components/load-when-visible';
import ConditionalWrapper from 'components/conditional-wrapper';
import { mapPageIds } from 'constants/test-constants';
import AppleMap from '@zoocasa/node-kit/map/apple/map';
import SatelliteControl from 'components/search-es/map/SatelliteControl';
import BaseMap from '../base-map';
import { drawInitialMap, cleanupMap } from 'utils/apple-maps';
import { trackEvent } from 'utils/google-tag-manager';
import {
  GTM_CLICK_ADDRESS_MODAL_MAP_SATELLITE_VIEW,
  GTM_CLICK_ADDRESS_MODAL_MAP_STREET_VIEW,
  GTM_CLICK_ADDRESS_PAGE_MAP_SATELLITE_VIEW,
  GTM_CLICK_ADDRESS_PAGE_MAP_STREET_VIEW,
} from 'constants/events';
import { useThemeContext } from 'contexts';
import MapZoomControls from './MapZoomControls';
import Button from 'components/control/button';
import ExpandIcon from 'components/icon/expand-icon-2';
import FoldIcon from 'components/icon/fold-icon';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';

import type { GeoJsonPoint } from '@zoocasa/node-kit/map/types';

type MapProps = {
  className?: string;
  listingId: number | string;
  trackEventLocation?: TrackEventLocation;
  position: GeoJsonPoint;
  address?: string;
  fullScreenOption?: boolean;
  isInteractive?: boolean;
  loadWhenVisible?: boolean;
};

export enum TrackEventLocation {
  Modal = 'Modal',
  Page = 'Page'
}

const Map = ({
  className,
  position,
  listingId,
  address,
  trackEventLocation = TrackEventLocation.Page,
  isInteractive = true,
  loadWhenVisible = true,
  fullScreenOption = true,
}: MapProps) => {
  const { t } = useTranslation(Namespaces.COMMON);

  // Use React's useId hook for stable, unique IDs
  const id = useId();
  const mapElementId = `listing-map-${listingId}-${id}`;
  const fullMapElementId = `listing-full-map-${listingId}-${id}`;

  const [mapShouldLoad, setMapShouldLoad] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const mapRef = useRef<AppleMap | null>(null);
  const fullMapRef = useRef<AppleMap | null>(null);
  const fullMapElementRef = useRef<HTMLDivElement | null>(null);
  const fullMapInitializingRef = useRef<boolean>(false);
  const { themeName } = useThemeContext();

  // Reset map loading when listing changes
  useEffect(() => {
    setMapShouldLoad(false);
  }, [listingId]);

  // Handle immediate loading if loadWhenVisible is false
  useEffect(() => {
    if (!loadWhenVisible) {
      setMapShouldLoad(true);
    }
  }, [loadWhenVisible]);

  const handleSatelliteAndStreetViewToggled = useCallback(() => {
    const _mapRef = isFullScreen ? fullMapRef : mapRef;

    if (typeof window === 'undefined' || !window.mapkit) {
      return;
    }

    if (_mapRef?.current?.map) {
      const currentMapType = _mapRef.current.map.mapType;
      if (currentMapType === mapkit.Map.MapTypes.Standard) {
        _mapRef.current.map.mapType = mapkit.Map.MapTypes.Satellite;
        trackSatelliteViewEventByLocation();
        return mapkit.Map.MapTypes.Satellite;
      } else if (currentMapType === mapkit.Map.MapTypes.Satellite) {
        _mapRef.current.map.mapType = mapkit.Map.MapTypes.Standard;
        trackStreetViewEventByLocation();
        return mapkit.Map.MapTypes.Standard;
      }

      // Return current map type if it's something else
      return currentMapType;
    }

    // Return undefined if map is not available - let SatelliteControl handle it
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullScreen]);

  const trackSatelliteViewEventByLocation = () => {
    if (trackEventLocation === TrackEventLocation.Page) {
      trackEvent(GTM_CLICK_ADDRESS_PAGE_MAP_SATELLITE_VIEW);
    } else if (trackEventLocation === TrackEventLocation.Modal) {
      trackEvent(GTM_CLICK_ADDRESS_MODAL_MAP_SATELLITE_VIEW);
    }
  };

  const trackStreetViewEventByLocation = () => {
    if (trackEventLocation === TrackEventLocation.Page) {
      trackEvent(GTM_CLICK_ADDRESS_PAGE_MAP_STREET_VIEW);
    } else if (trackEventLocation === TrackEventLocation.Modal) {
      trackEvent(GTM_CLICK_ADDRESS_MODAL_MAP_STREET_VIEW);
    }
  };

  const handleMapLoaded = useCallback((mapInstance: AppleMap) => {
    mapRef.current = mapInstance;
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: any) => {
      if (e.key === 'Escape') {
        handleCloseMap();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullScreen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupMap(fullMapElementId);
      cleanupMap(mapElementId);
    };
  }, [fullMapElementId, mapElementId]);

  const initializeFullScreenMap = useCallback(() => {
    if (fullMapElementRef.current && isFullScreen && !fullMapInitializingRef.current) {
      fullMapInitializingRef.current = true;
      drawInitialMap(fullMapElementId, position, isInteractive, themeName)
        .then(mapInstance => {
          fullMapRef.current = mapInstance;
          if (mapRef.current?.map.mapType) {
            fullMapRef.current.map.mapType = mapRef.current.map.mapType;
          }
          fullMapInitializingRef.current = false;
        })
        .catch((error: any) => {
          console.error('Error loading fullscreen map:', error);
          fullMapInitializingRef.current = false;
        });
    }
  }, [fullMapElementId, position, isInteractive, themeName, isFullScreen]);

  // Initialize map when the full screen state changes and element is available
  useEffect(() => {
    if (isFullScreen && fullMapElementRef.current) {
      initializeFullScreenMap();
    }
  }, [isFullScreen, initializeFullScreenMap]);

  // Function to open the full-screen map
  const handleOpenFullScreen = () => {
    if (TrackEventLocation.Page) {
      trackEvent('UiAddressButMapClk');
      trackEvent('UiAddressMapModFullScreenView');
      trackEvent('UiAddressMapButFullMapClk');
    } else {
      trackEvent('UiListingMButMapClk');
      trackEvent('UiListingMMapModFullScreenView');
      trackEvent('UiListingMMapButFullMapClk');
    }
    setIsFullScreen(true);
  };

  const handleCloseMap = () => {
    // Sync map type back to the original map before closing
    if (mapRef.current && fullMapRef.current) {
      mapRef.current.map.mapType = fullMapRef.current.map.mapType;
    }

    // Clean up fullscreen map
    cleanupMap(fullMapElementId);
    fullMapRef.current = null;

    setIsFullScreen(false);
    fullMapInitializingRef.current = false;
  };

  const zoomIn = () => {
    if (isFullScreen) {
      // In fullscreen mode, only zoom if the fullscreen map is ready
      if (fullMapRef.current) {
        fullMapRef.current.zoomIn();
      }
    } else {
      // In normal mode, zoom the regular map
      if (mapRef.current) {
        mapRef.current.zoomIn();
      }
    }
  };

  const zoomOut = () => {
    if (isFullScreen) {
      // In fullscreen mode, only zoom if the fullscreen map is ready
      if (fullMapRef.current) {
        fullMapRef.current.zoomOut();
      }
    } else {
      // In normal mode, zoom the regular map
      if (mapRef.current) {
        mapRef.current.zoomOut();
      }
    }
  };

  return (
    <>
      {isFullScreen && (
        <div className={styles['full-screen-overlay']}>
          <div className={styles['full-screen-map-container']}>
            <div className={styles['title-container']}>
              <h1>{address}</h1>
              <button className={styles['close-button']} onClick={() => {
                const eventName = trackEventLocation === TrackEventLocation.Page ? 'UiAddressMapButXClk' : 'UiListingMMapButXClk';
                trackEvent(eventName);
                handleCloseMap();
              }}>
                &times;
              </button>
            </div>
            <SatelliteControl
              className={styles['satellite-toggle-button']}
              onViewToggle={handleSatelliteAndStreetViewToggled}
            />
            <MapZoomControls zoomIn={zoomIn} zoomOut={zoomOut} tenantName={themeName} />
            <Button label={t(LOCALE_COMMON_PATHS.MAP_CLOSE_MAP, { defaultValue: 'Close Map' })} theme={'secondary'} Icon={FoldIcon} className={styles['full-map-button']} onClick={() => {
              const eventName = trackEventLocation === TrackEventLocation.Page ? 'UiAddressMapButCloseMapClk' : 'UiListingMMapButCloseMapClk';
              trackEvent(eventName);
              handleCloseMap();
            }} />
            <div
              id={fullMapElementId}
              className={styles['full-screen-map']}
              ref={element => {
                fullMapElementRef.current = element;
                if (element && isFullScreen) {
                  initializeFullScreenMap();
                }
              }}
            />
          </div>
        </div>
      )}

      <ConditionalWrapper
        condition={loadWhenVisible}
        wrapper={children => (
          <LoadWhenVisible
            key={id}
            onValueChange={() => setMapShouldLoad(true)}
          >
            {children}
          </LoadWhenVisible>
        )}
      >
        <div className={styles.component}>
          <BaseMap
            key={id}
            position={position}
            mapElementId={mapElementId}
            isInteractive={isInteractive}
            shouldLoad={mapShouldLoad}
            address={address}
            className={className}
            onMapLoaded={handleMapLoaded}
            testId={mapPageIds.listingMap}
          >
            <SatelliteControl className={styles['custom-position']} onViewToggle={handleSatelliteAndStreetViewToggled} />
            {fullScreenOption &&
                <>
                  <MapZoomControls zoomIn={zoomIn} zoomOut={zoomOut} tenantName={themeName} />
                  <Button label={t(LOCALE_COMMON_PATHS.MAP_FULL_MAP, { defaultValue: 'Full Map' })} theme={'secondary'} Icon={ExpandIcon} className={styles['full-map-button']} onClick={handleOpenFullScreen} />
                </>}
          </BaseMap>
        </div>
      </ConditionalWrapper>
    </>
  );
};

export default Map;